import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Row } from '@kerfjs/ui/row';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';
import { Plus } from 'lucide';

import {
  chatActiveId,
  chatConversations,
  chatSortDirection,
  chatSortField,
  closedChatsExpanded,
  newChatDialogOpen,
  shellSearch,
  shellSearchTokens,
} from '../../../data/state.js';
import { sortProjectChats } from '../../../lib/project-chat-sort.js';
import { filterChatsForShellSearch } from '../../../lib/shell-search.js';
import { ChatSidebarRow } from './chat-sidebar-row.js';
import { ChatSortMenu } from './chat-sort-menu.js';
import { NewChatDialog } from './new-chat-dialog.js';

/** Project chat navigation with open/closed groups and production row actions. */
export function ChatSidebar(): SafeHtml {
  const filtered = filterChatsForShellSearch(chatConversations.value, shellSearch.value, shellSearchTokens.value);
  const items = sortProjectChats(filtered, chatSortField.value, chatSortDirection.value);
  const openChats = items.filter((item) => item.closedAt === null);
  const closedChats = items.filter((item) => item.closedAt !== null);
  const selectedId = chatActiveId.value;
  return (
    <List className="chat-sidebar">
      <ListInsetControl>
        <Button
          action="new-chat-open"
          size="compact"
          tone="brand"
          emphasis="primary"
          icon={<LucideIcon icon={Plus} name="" />}
        >
          New Chat…
        </Button>
      </ListInsetControl>
      {items.length === 0 ? (
        <Row controlInsets="rl">
          <Text tone="quiet" size="compact">
            No chats yet.
          </Text>
        </Row>
      ) : (
        <>
          <List className="chat-group--open">
            <Row vAlign="middle" gap="2xs">
              <List flex>
                <ListHeader
                  label="Open Chats"
                  count={openChats.length}
                  countLabel={`${String(openChats.length)} chats`}
                />
              </List>
              <ChatSortMenu />
            </Row>
            {openChats.length === 0 ? (
              <Text tone="quiet" size="compact">
                No open chats.
              </Text>
            ) : (
              openChats.map((conversation) => (
                <ChatSidebarRow conversation={conversation} closed={false} selectedId={selectedId} />
              ))
            )}
          </List>
          {closedChats.length > 0 ? (
            <List className="chat-group--closed">
              <ListHeader
                label="Closed Chats"
                count={closedChats.length}
                countLabel={`${String(closedChats.length)} closed`}
                toggle={true}
                expanded={closedChatsExpanded.value}
                action="toggle-closed-chats"
              />
              {closedChatsExpanded.value
                ? closedChats.map((conversation) => (
                    <ChatSidebarRow conversation={conversation} closed selectedId={selectedId} />
                  ))
                : null}
            </List>
          ) : null}
        </>
      )}
      {newChatDialogOpen.value ? <NewChatDialog /> : null}
    </List>
  );
}
